import { CSSProperties } from "react";
import styled from "@emotion/styled";
import { GrFileUploadProgressListItemProps, GrIcon, GrIconButton, GrProgressCircle, GrStack, GrTypography, palette } from "@jnpmedi/grebob";
import { convertFileTypeToGrIconType } from "@newLibs/converting";
import { multiLineEllipsis } from "@assets/styles/layout";

type CardSize = "large" | "medium";

type FileUploadResultCardProps = {
  fileName: string;
  onReset: () => void;
  uploadProgressOptions?: Pick<GrFileUploadProgressListItemProps, "progress" | "status">;
  size?: CardSize;
  fileType?: string;
  style?: CSSProperties;
};

export function FileUploadResultCard({
  fileName,
  onReset,
  uploadProgressOptions,
  size = "medium",
  fileType,
  style,
}: FileUploadResultCardProps) {
  const xMarkIconButton = (
    <GrIconButton key="x-mark-icon-btn" color="grey060" iconType="x-mark" size={14} onClick={onReset} style={{ padding: "5px" }} />
  );
  const renderProgressIcon = () => {
    if (!uploadProgressOptions) {
      return xMarkIconButton;
    }

    const { status, progress } = uploadProgressOptions;

    switch (status) {
      case "WAIT":
      case "UPLOADING":
        return <GrProgressCircle key="in-progress-icon" value={progress} size={14} hasBackgroundColor thickness={10} />;
      case "DONE":
      case "ERROR":
        return xMarkIconButton;

      default:
        throw new Error("Not supported enum");
    }
  };

  return (
    <StyledFileUploadResultCard
      alignItems="center"
      justifyContent="space-between"
      border={`1px solid ${
        !!uploadProgressOptions
          ? uploadProgressOptions.status === "ERROR"
            ? palette["negative-primary"]
            : palette["grey020"]
          : palette["grey020"]
      }`}
      borderRadius="6px"
      size={size}
      style={style}
    >
      <GrStack gap={size === "large" ? "8px" : "6px"} alignItems="center">
        <GrIcon size={size === "large" ? 20 : 16} type={convertFileTypeToGrIconType(fileType?.toLowerCase())} />
        <GrTypography
          type={size === "large" ? "Content.Body04" : "Content.Body03"}
          color={!uploadProgressOptions ? "grey090" : ["WAIT", "UPLOADING"].includes(uploadProgressOptions.status) ? "grey030" : "grey090"}
          css={multiLineEllipsis(1)}
          title={fileName}
        >
          {fileName}
        </GrTypography>
      </GrStack>

      {renderProgressIcon()}
    </StyledFileUploadResultCard>
  );
}

const StyledFileUploadResultCard = styled(GrStack)<Pick<FileUploadResultCardProps, "size">>(({ size }) => ({
  gap: size === "large" ? "12px" : "8px",
  height: size === "large" ? "48px" : "32px",
  padding: size === "large" ? "0px 12px" : "0px 8px",
}));
